import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import { defaultHourForNthPlanned, formatDayKey, localOccurrenceAt } from '../trainingLog/plannerUtils';
import {
  cpPercentHeatmapColor,
  endlessStyle,
  runningSegmentBarCssColor,
  splitWeight,
} from '../../utils/runningWorkoutVizShared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { CalendarPlus, Copy, Flame, Loader2, Pencil, Star } from 'lucide-react';
import RecordingModeBadge from './RecordingModeBadge';
import { flattenStructureForViz } from '../../utils/runningWorkoutFlatten';
import {
  estimateBpmForSegment,
  estimatePaceSecPerKm,
  formatDistanceFromMeters,
} from '../../utils/runningSegmentLabels';
import { segmentEffectiveDurationSec } from '../../utils/runningWorkoutEditorSummary';
import { useRunningWorkoutCpPreview } from '../../hooks/useRunningWorkoutCpPreview';

function formatDurationSec(sec) {
  if (sec == null || !Number.isFinite(Number(sec)) || Number(sec) <= 0) return '—';
  const s = Math.round(Number(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function formatDistanceM(m) {
  if (m == null || !Number.isFinite(Number(m)) || Number(m) <= 0) return '—';
  const km = Number(m) / 1000;
  if (km >= 1) return `${km.toFixed(2)} km`;
  const mi = Number(m) / 1609.344;
  if (mi >= 0.25 && Math.abs(mi * 1609.344 - Number(m)) < 2) return `${mi.toFixed(2)} mi`;
  return `${Math.round(Number(m))} m`;
}

/** @param {number|string|null|undefined} qty @param {string|null|undefined} unitRaw */
function formatDistanceQtyUnit(qty, unitRaw) {
  if (qty == null || !Number.isFinite(Number(qty)) || Number(qty) <= 0) return '—';
  const n = Number(qty);
  const u = String(unitRaw || 'meter').toLowerCase().trim();
  const decimals = Number.isInteger(n) && Math.abs(n - Math.round(n)) < 1e-6 ? 0 : 2;
  const numStr = decimals ? n.toFixed(2) : String(Math.round(n));
  if (u === 'mile' || u === 'miles' || u === 'mi') return `${numStr} mi`;
  if (u === 'kilometer' || u === 'km') return `${numStr} km`;
  if (u === 'yard' || u === 'yards' || u === 'yd') return `${numStr} yd`;
  if (u === 'foot' || u === 'feet' || u === 'ft') return `${numStr} ft`;
  if (u === 'meter' || u === 'm') return `${numStr} m`;
  return `${numStr} ${unitRaw || 'm'}`;
}

const CP_CHART_MIN = 25;
const CP_CHART_MAX = 150;

function barHeightPercentFromCp(cp) {
  const v = cp != null && Number.isFinite(Number(cp)) ? Number(cp) : 75;
  const clamped = Math.min(CP_CHART_MAX, Math.max(CP_CHART_MIN, v));
  return ((clamped - CP_CHART_MIN) / (CP_CHART_MAX - CP_CHART_MIN)) * 100;
}

function cpReferenceLineBottomPercent() {
  return ((100 - CP_CHART_MIN) / (CP_CHART_MAX - CP_CHART_MIN)) * 100;
}

/** @param {object} s split row */
function splitUsesDistancePrimary(s) {
  return String(s.split_measure_mode || '').toLowerCase() === 'distance';
}

function segmentPrimaryMeasure(s, t) {
  if (splitUsesDistancePrimary(s)) {
    if (s.distance_quantity != null && s.distance_unit_selected) {
      return formatDistanceQtyUnit(s.distance_quantity, s.distance_unit_selected);
    }
    const du = s.distance_display_unit;
    if (du === 'miles' || du === 'km') {
      const formatted = formatDistanceFromMeters(Number(s.distance_m), du, t);
      if (formatted) return formatted;
    }
    return formatDistanceM(s.distance_m);
  }
  return formatDurationSec(s.duration_sec);
}

function formatPaceSecPerKm(secPerKm) {
  if (secPerKm == null || !Number.isFinite(secPerKm) || secPerKm <= 0) return '—';
  const t = Math.round(secPerKm);
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${String(s).padStart(2, '0')}/km`;
}

function mapVizSplitToDetailSplit(s, idx, keyPrefix, extra = {}) {
  return {
    key: `${keyPrefix}-${idx}`,
    duration_sec: s.duration_sec,
    distance_m: s.distance_m,
    split_measure_mode: s.split_measure_mode,
    distance_quantity: s.distance_quantity,
    distance_unit_selected: s.distance_unit_selected,
    run_intensity_class: s.run_intensity_class,
    intensity_kind: s.intensity_kind,
    cp_percent: s.cp_percent != null ? Number(s.cp_percent) : null,
    cp_percent_min: s.cp_percent_min != null ? Number(s.cp_percent_min) : null,
    cp_percent_max: s.cp_percent_max != null ? Number(s.cp_percent_max) : null,
    repeat_group_id: s.repeat_group_id ?? null,
    repeat_lap_index: s.repeat_lap_index ?? null,
    slot_in_lap: s.slot_in_lap ?? null,
    sort_order: s.sort_order ?? idx,
    distance_display_unit:
      s.distance_display_unit === 'miles' || s.distance_display_unit === 'km'
        ? s.distance_display_unit
        : null,
    endless: !!s.endless,
    ...extra,
  };
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {'library'|'instance'|'trainingExercise'|'preloaded'} props.mode
 * @param {number|null} props.librarySessionId
 * @param {number|null} props.instanceId
 * @param {object|null} [props.trainingExercise] — `training_exercises` row with `structure` (catalog / library picker)
 * @param {Array} [props.preloadedSplits] — pre-computed splits (mode="preloaded"); skips API fetch
 * @param {string} [props.fallbackTitle]
 * @param {string} [props.preloadedDescription] — description for preloaded mode
 * @param {() => void} [props.onEditTrainingExercise] — library exercise: open graphic editor
 * @param {() => void|Promise<void>} [props.onCopyTrainingExercise] — library exercise: duplicate into library editor
 * @param {() => void} [props.onEditInstance] — instance exercise: open segment editor
 */
export default function RunningExerciseDetail({
  open,
  onOpenChange,
  mode,
  librarySessionId,
  instanceId,
  trainingExercise,
  preloadedSplits,
  preloadedDescription,
  fallbackTitle,
  onEditTrainingExercise,
  onCopyTrainingExercise,
  onEditInstance,
}) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [splits, setSplits] = useState([]);
  const [cpWatts, setCpWatts] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [recordingMode, setRecordingMode] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarDayYmd, setCalendarDayYmd] = useState(() => formatDayKey(new Date()));
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  useEffect(() => {
    if (!open || mode !== 'trainingExercise' || !trainingExercise?.id) return;
    if (trainingExercise.is_favorite != null) {
      setIsFavorite(!!trainingExercise.is_favorite);
      return;
    }
    let cancelled = false;
    api
      .get(`/api/training/exercises/${trainingExercise.id}`)
      .then((res) => {
        if (!cancelled) setIsFavorite(!!res.data?.exercise?.is_favorite);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, mode, trainingExercise?.id, trainingExercise?.is_favorite]);

  const toggleFavorite = async () => {
    if (!trainingExercise?.id) return;
    const next = !isFavorite;
    try {
      await api.put(`/api/training/exercises/${trainingExercise.id}/favorite`, { favorite: next });
      setIsFavorite(next);
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (e) {
      console.error(e);
    }
  };

  const openCalendarModal = () => {
    setCalendarDayYmd(formatDayKey(new Date()));
    setCalendarOpen(true);
  };

  const handleCopyExercise = async () => {
    if (!onCopyTrainingExercise) return;
    setCopyBusy(true);
    try {
      await onCopyTrainingExercise();
    } finally {
      setCopyBusy(false);
    }
  };

  const handleAddToCalendar = async () => {
    if (!trainingExercise?.id) return;
    setCalendarBusy(true);
    try {
      const sessRes = await api.post(`/api/training/exercises/${trainingExercise.id}/planner-session`);
      const trainingSessionId = sessRes.data?.trainingSessionId;
      if (!trainingSessionId) {
        throw new Error('no_session');
      }
      const hour = defaultHourForNthPlanned(0, calendarDayYmd);
      const occurredAt = localOccurrenceAt(calendarDayYmd, hour, 0).toISOString();
      await api.post('/api/training-log/items', {
        typeKey: 'planned_session',
        trainingSessionId,
        occurredAt,
        title: title || null,
        summary: description || null,
      });
      setCalendarOpen(false);
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (e) {
      console.error(e);
    } finally {
      setCalendarBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [baselineRes, settingsRes] = await Promise.all([
          api.get('/api/training/running-cp-baseline').catch(() => ({ data: {} })),
          api.get('/api/users/settings').catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setBaseline(baselineRes.data || {});
        const settingsCp = settingsRes.data?.runningCriticalPowerWatts;
        if (settingsCp != null && Number.isFinite(Number(settingsCp))) {
          setCpWatts(Number(settingsCp));
        } else if (baselineRes.data?.cpWatts != null) {
          setCpWatts(Number(baselineRes.data.cpWatts));
        } else {
          setCpWatts(null);
        }

        if (mode === 'instance' && instanceId) {
          const res = await api.get(`/api/user-running-exercises/${instanceId}`);
          if (cancelled) return;
          const ex = res.data?.exercise;
          const segs = res.data?.segments || [];
          setTitle(ex?.title || fallbackTitle || '');
          setDescription(ex?.description || '');
          setRecordingMode(ex?.recording_mode || null);
          setSplits(
            segs.map((s, idx) =>
              mapVizSplitToDetailSplit(
                {
                  ...s,
                  split_measure_mode: s.split_measure_mode === 'distance' ? 'distance' : 'duration',
                },
                idx,
                `s-${s.id}`,
                { id: s.id }
              )
            )
          );
          if (res.data?.resolved_cp_watts != null) setCpWatts(Number(res.data.resolved_cp_watts));
        } else if (mode === 'library' && librarySessionId) {
          const res = await api.get(`/api/training/sessions/${librarySessionId}`);
          if (cancelled) return;
          const session = res.data?.session;
          setTitle(session?.name || fallbackTitle || '');
          setDescription(session?.description || '');
          const blocks = session?.blocks || [];
          let flat = [];
          for (const b of blocks) {
            const struct = b?.library_exercise?.structure;
            if (!struct?.groups) continue;
            flat = flattenStructureForViz(struct);
            break;
          }
          setSplits(flat.map((s, idx) => mapVizSplitToDetailSplit(s, idx, 'l')));
        } else if (mode === 'preloaded' && Array.isArray(preloadedSplits)) {
          setTitle(fallbackTitle || '');
          setDescription(preloadedDescription || '');
          setSplits(preloadedSplits.map((s, idx) => mapVizSplitToDetailSplit(s, idx, 'pl')));
        } else if (mode === 'trainingExercise' && trainingExercise) {
          setTitle(trainingExercise.name || fallbackTitle || '');
          setDescription((trainingExercise.notes && String(trainingExercise.notes).trim()) || '');
          const flat = flattenStructureForViz(trainingExercise.structure);
          setSplits(flat.map((s, idx) => mapVizSplitToDetailSplit(s, idx, 'te')));
        } else {
          setTitle(fallbackTitle || '');
          setDescription('');
          setSplits([]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setSplits([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, instanceId, librarySessionId, fallbackTitle, trainingExercise, preloadedSplits, preloadedDescription]);

  const chartRows = useMemo(() => {
    return splits.map((s, i) => {
      const cp = s.cp_percent != null ? Number(s.cp_percent) : 75;
      const w = splitWeight(s);
      const classFill = runningSegmentBarCssColor(s.run_intensity_class);
      return {
        name: `S:${i + 1}`,
        cp,
        fill:
          classFill ?? cpPercentHeatmapColor(Math.min(CP_CHART_MAX, Math.max(0, cp))),
        flexW: Math.max(0.02, w),
        barH: barHeightPercentFromCp(cp),
        hoverTitle: `${segmentPrimaryMeasure(s, t)} · ${cp}% CP`,
        ...s,
      };
    });
  }, [splits, t]);

  const chartWeightSum = useMemo(
    () => chartRows.reduce((a, r) => a + r.flexW, 0) || 1,
    [chartRows]
  );

  const repeatInfo = useMemo(() => {
    const byG = {};
    for (const s of splits) {
      if (s.repeat_group_id == null) continue;
      const g = s.repeat_group_id;
      if (!byG[g]) byG[g] = new Set();
      byG[g].add(s.repeat_lap_index);
    }
    const counts = {};
    for (const g of Object.keys(byG)) {
      counts[g] = byG[g].size;
    }
    return counts;
  }, [splits]);

  const bpmAtCp = baseline?.bpmAtCp;

  const { summary: runSummary, thresholdPaceSecPerKm, thresholdBpm, paceEstimateOpts, hrEstimateOpts } =
    useRunningWorkoutCpPreview(splits, t);
  const hasCpSummary =
    runSummary &&
    runSummary.timeLabel &&
    runSummary.timeLabel !== '—' &&
    splits.length > 0;
  const durationStr = hasCpSummary ? runSummary.timeLabel : null;
  const distanceTag =
    hasCpSummary && runSummary.distanceLabel && runSummary.distanceLabel !== '—'
      ? runSummary.distanceLabel
      : null;
  const showEstimateFlags = splits.length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          priority="nested"
          className="max-h-[min(92vh,900px)] max-w-[min(95vw,720px)] overflow-y-auto"
        >
        <DialogHeader className="flex flex-col gap-3 space-y-0 pr-12 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3 sm:pr-14">
          <div className="min-w-0 flex-1">
            <DialogTitle className="flex items-center gap-2 text-left">
              {title || t('runningExercise.detailTitle')}
              {mode === 'instance' && <RecordingModeBadge mode={recordingMode} />}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('runningExercise.detailDialogDesc')}</DialogDescription>
          </div>
          <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-1.5 sm:max-w-[min(100%,calc(100%-2.75rem))]">
            {mode === 'trainingExercise' && trainingExercise?.id && !loading && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-foreground"
                    onClick={toggleFavorite}
                    aria-pressed={isFavorite}
                    aria-label={t('runningExercise.favoriteToggleAria')}
                  >
                    <Star
                      className={cn(
                        'h-4 w-4',
                        isFavorite ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('runningExercise.favoriteTooltip')}</TooltipContent>
              </Tooltip>
            )}
            {mode === 'trainingExercise' && trainingExercise?.id && !loading && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5"
                onClick={openCalendarModal}
              >
                <CalendarPlus className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{t('runningExercise.addToCalendar')}</span>
                <span className="sm:hidden">{t('runningExercise.addToCalendarShort')}</span>
              </Button>
            )}
            {mode === 'trainingExercise' && typeof onCopyTrainingExercise === 'function' && !loading && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2"
                disabled={copyBusy}
                onClick={handleCopyExercise}
              >
                {copyBusy ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                ) : (
                  <Copy className="h-4 w-4 shrink-0" />
                )}
                <span className="hidden sm:inline">{t('runningExercise.copyExercise')}</span>
                <span className="sm:hidden">{t('runningExercise.copyExerciseShort')}</span>
              </Button>
            )}
            {mode === 'trainingExercise' && typeof onEditTrainingExercise === 'function' && !loading && (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="shrink-0 gap-2"
                onClick={onEditTrainingExercise}
              >
                <Pencil className="h-4 w-4" />
                {t('runningExercise.editLibraryWorkout')}
              </Button>
            )}
            {mode === 'instance' && typeof onEditInstance === 'function' && !loading && (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="shrink-0 gap-2"
                onClick={onEditInstance}
              >
                <Pencil className="h-4 w-4" />
                {t('training.editExercise')}
              </Button>
            )}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {!!description && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{description}</p>
            )}
            {!cpWatts && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{t('runningExercise.setCpHint')}</p>
            )}
            {!!cpWatts && (
              <p className="text-xs text-muted-foreground">
                {t('runningExercise.cpLine', { watts: Math.round(cpWatts) })}
              </p>
            )}
            {showEstimateFlags && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {durationStr && (
                  <span className="inline-flex items-center rounded-full border border-border/80 bg-background/60 px-2 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                    {durationStr}
                  </span>
                )}
                {distanceTag && (
                  <span className="inline-flex items-center rounded-full border border-border/80 bg-background/40 px-2 py-0.5 text-[10px] text-muted-foreground tabular-nums">
                    {distanceTag}
                  </span>
                )}
                <span className="inline-flex items-center rounded-full border border-border/80 bg-background/40 px-2 py-0.5 text-[10px] capitalize text-muted-foreground">
                  {t('training.typeRunning')}
                </span>
                {hasCpSummary && runSummary.weightedCpLabel && runSummary.weightedCpLabel !== '—' && (
                  <span
                    className="inline-flex max-w-[140px] items-center truncate rounded-full border border-border/80 bg-background/40 px-2 py-0.5 text-[10px] text-muted-foreground"
                    title={runSummary.weightedCpLabel}
                  >
                    {runSummary.weightedCpLabel}
                  </span>
                )}
                {runSummary?.sufferScoreEstimate != null && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-background/40 px-2 py-0.5 text-[10px] text-muted-foreground tabular-nums">
                    <Flame className="h-2.5 w-2.5 text-orange-500/80" />
                    {runSummary.sufferScoreEstimate}
                  </span>
                )}
              </div>
            )}
            {chartRows.length > 0 && (
              <div className="flex h-[220px] w-full gap-1 rounded-lg border border-border bg-card p-2">
                <div className="flex w-9 shrink-0 flex-col">
                  <span className="text-[9px] font-medium text-muted-foreground">% CP</span>
                  <div className="flex flex-1 flex-col justify-between pb-6 pt-1 text-[9px] tabular-nums text-muted-foreground">
                    <span>{CP_CHART_MAX}</span>
                    <span>95</span>
                    <span>60</span>
                    <span>{CP_CHART_MIN}</span>
                  </div>
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="relative min-h-[150px] flex-1">
                    <div
                      className="pointer-events-none absolute left-0 right-0 z-[1] border-t border-dashed border-muted-foreground/55"
                      style={{ bottom: `${cpReferenceLineBottomPercent()}%` }}
                    />
                    {!!cpWatts && (
                      <span className="pointer-events-none absolute right-0 top-0 z-[2] text-[9px] text-foreground">
                        CP {Math.round(cpWatts)} W
                      </span>
                    )}
                    <div
                      className="absolute inset-0 flex items-end gap-px bg-[repeating-linear-gradient(180deg,transparent,transparent_23%,hsl(var(--muted-foreground)/0.08)_23%,hsl(var(--muted-foreground)/0.08)_24%)] pt-2"
                      role="img"
                      aria-label={t('runningExercise.chartAria')}
                    >
                      {chartRows.map((entry) => (
                        <div
                          key={entry.key}
                          className="flex min-w-[3px] flex-col justify-end self-stretch"
                          style={{ flex: `${entry.flexW / chartWeightSum} 1 0` }}
                          title={entry.hoverTitle}
                        >
                          <div
                            className="w-full min-h-[4px] rounded-[2px]"
                            style={{ height: `${entry.barH}%`, ...(entry.endless ? endlessStyle(entry.fill) : { backgroundColor: entry.fill }) }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-1 flex gap-px">
                    {chartRows.map((entry) => (
                      <div
                        key={`${entry.key}-x`}
                        className="min-w-0 truncate text-center text-[9px] text-muted-foreground"
                        style={{ flex: `${entry.flexW / chartWeightSum} 1 0` }}
                      >
                        {entry.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-muted-foreground">
                    <th className="text-left p-2 font-medium">{t('runningExercise.colSplit')}</th>
                    <th className="text-left p-2 font-medium">{t('runningExercise.colSegment')}</th>
                    <th className="text-right p-2 font-medium">{t('runningExercise.colWatts')}</th>
                    <th className="text-right p-2 font-medium">{t('runningExercise.colCpRange')}</th>
                    <th className="text-right p-2 font-medium">{t('runningExercise.colPaceEst')}</th>
                    <th className="text-right p-2 font-medium">{t('runningExercise.colBpmEst')}</th>
                  </tr>
                </thead>
                <tbody>
                  {splits.map((s, i) => {
                    const gmin = s.cp_percent_min ?? s.cp_percent;
                    const gmax = s.cp_percent_max ?? s.cp_percent;
                    const wLow =
                      cpWatts != null && gmin != null ? Math.round((cpWatts * Number(gmin)) / 100) : null;
                    const wHigh =
                      cpWatts != null && gmax != null ? Math.round((cpWatts * Number(gmax)) / 100) : null;
                    const pace = estimatePaceSecPerKm(s, thresholdPaceSecPerKm, paceEstimateOpts);
                    const segDurSec = segmentEffectiveDurationSec(
                      s,
                      thresholdPaceSecPerKm,
                      paceEstimateOpts
                    );
                    const bpm = estimateBpmForSegment(s, thresholdBpm ?? bpmAtCp, {
                      ...hrEstimateOpts,
                      durationSec: segDurSec > 0 ? segDurSec : null,
                    });
                    const showRepeatBadge =
                      s.repeat_group_id != null &&
                      Number(s.repeat_lap_index) === 0 &&
                      Number(s.slot_in_lap) === 0 &&
                      repeatInfo[s.repeat_group_id] > 1;
                    const rep = showRepeatBadge ? repeatInfo[s.repeat_group_id] : null;
                    return (
                      <tr key={s.key || i} className="border-b border-border/60">
                        <td className="p-2 align-top">
                          <span
                            className={cn(
                              'inline-flex min-w-[2.25rem] justify-center rounded px-1 py-0.5 text-[10px] font-medium text-primary-foreground',
                              s.run_intensity_class === 'warmup' && 'bg-emerald-600',
                              s.run_intensity_class === 'rest' && 'bg-muted-foreground/50 text-background',
                              s.run_intensity_class !== 'warmup' &&
                                s.run_intensity_class !== 'rest' &&
                                'bg-primary'
                            )}
                          >
                            {rep ? `×${rep}` : `S:${i + 1}`}
                          </span>
                        </td>
                        <td className="p-2 align-top text-foreground">
                          <div>
                            <span className="tabular-nums">{segmentPrimaryMeasure(s, t)}</span>{' '}
                            <span className="capitalize text-muted-foreground">
                              {s.run_intensity_class || 'run'}
                            </span>
                          </div>
                          {splitUsesDistancePrimary(s) && segDurSec > 0 && (
                            <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                              {t('runningExercise.estimatedTimeApprox', {
                                time: formatDurationSec(segDurSec),
                              })}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          {wLow != null && wHigh != null ? `${wLow} – ${wHigh}` : '—'}
                        </td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">
                          {gmin != null && gmax != null ? `${gmin} – ${gmax}%` : '—'}
                        </td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">
                          {pace != null ? (
                            <span title={t('runningExercise.estimatedHint')}>{formatPaceSecPerKm(pace)}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">
                          {bpm != null ? (
                            <span title={t('runningExercise.estimatedHint')}>{bpm}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(thresholdPaceSecPerKm != null || thresholdBpm != null || bpmAtCp) && (
              <p className="text-[10px] text-muted-foreground">{t('runningExercise.baselineFootnote')}</p>
            )}
          </div>
        )}
        </DialogContent>
      </Dialog>

      <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
        <DialogContent
          priority="nested"
          overlayClassName="z-[10060]"
          className="z-[10060] max-w-[min(90vw,400px)]"
        >
          <DialogHeader>
            <DialogTitle>{t('runningExercise.addToCalendarDialogTitle')}</DialogTitle>
            <DialogDescription>{t('runningExercise.addToCalendarDialogDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="library-add-calendar-date">{t('runningExercise.addToCalendarDateLabel')}</Label>
            <Input
              id="library-add-calendar-date"
              type="date"
              className="font-mono tabular-nums"
              value={calendarDayYmd}
              onChange={(e) => setCalendarDayYmd(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCalendarOpen(false)} disabled={calendarBusy}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={handleAddToCalendar} disabled={calendarBusy}>
              {calendarBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('runningExercise.addToCalendarSubmit')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

